import { useState } from 'react'
import { useNotificationContext } from '../../contexts/NotificationContext'
import { CATEGORY_LABELS, ShiftCategory, ShiftTemplate, formatTimeShort } from './utils'

interface Props {
  token: string
  clientId: number
  templates: ShiftTemplate[]
  onClose: () => void
  onChanged: () => void
}

const DEFAULT_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#64748b']

export default function ShiftTemplatesModal({ token, clientId, templates, onClose, onChanged }: Props) {
  const { showError, showSuccess } = useNotificationContext()
  const [editing, setEditing] = useState<Partial<ShiftTemplate> | null>(null)
  const [busy, setBusy] = useState(false)

  const startNew = () => {
    setEditing({
      client_id: clientId,
      name: '',
      category: 'shift',
      start_time: '07:00',
      end_time: '15:00',
      crosses_midnight: false,
      color: '#3b82f6',
      description: '',
      is_active: true,
    })
  }

  const startEdit = (t: ShiftTemplate) => {
    setEditing({ ...t })
  }

  const handleSave = async () => {
    if (!editing) return
    if (!editing.name?.trim()) {
      showError('Podaj nazwę szablonu')
      return
    }
    setBusy(true)
    try {
      const body = {
        client_id: clientId,
        name: editing.name?.trim(),
        category: editing.category || 'shift',
        start_time: editing.start_time,
        end_time: editing.end_time,
        crosses_midnight: !!editing.crosses_midnight,
        color: editing.color || '#3b82f6',
        description: editing.description || null,
        is_active: editing.is_active !== false,
      }
      const url = editing.id
        ? `/api/schedule/shift-templates/${editing.id}`
        : `/api/schedule/shift-templates`
      const method = editing.id ? 'PUT' : 'POST'
      const r = await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      if (!r.ok) {
        const txt = await r.text()
        throw new Error(txt)
      }
      showSuccess(editing.id ? 'Szablon zaktualizowany' : 'Szablon dodany')
      setEditing(null)
      onChanged()
    } catch (e: any) {
      showError(`Błąd: ${e?.message || e}`)
    } finally {
      setBusy(false)
    }
  }

  const handleDelete = async (t: ShiftTemplate, hard: boolean) => {
    const confirmMsg = hard
      ? `Usunąć trwale szablon "${t.name}" WRAZ ze wszystkimi przypisaniami?`
      : `Dezaktywować szablon "${t.name}"? (Historyczne wpisy pozostaną)`
    if (!window.confirm(confirmMsg)) return
    setBusy(true)
    try {
      const r = await fetch(`/api/schedule/shift-templates/${t.id}?hard=${hard}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!r.ok && r.status !== 204) {
        throw new Error(await r.text())
      }
      showSuccess('Wykonano')
      onChanged()
    } catch (e: any) {
      showError(`Błąd: ${e?.message || e}`)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-3">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">🎨 Szablony zmian</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">✕</button>
        </div>

        <div className="flex-1 overflow-auto">
          {!editing && (
            <div className="p-3 space-y-2">
              <div className="flex justify-end">
                <button onClick={startNew} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700">
                  + Nowy szablon
                </button>
              </div>
              {templates.length === 0 && (
                <p className="text-sm text-gray-500 italic">Brak szablonów. Dodaj pierwszy.</p>
              )}
              <ul className="divide-y divide-gray-200 border border-gray-200 rounded">
                {templates.map(t => (
                  <li key={t.id} className="p-2 flex items-center gap-3">
                    <span
                      className={`inline-block w-5 h-5 rounded ${t.category === 'oncall' ? 'border-2 border-dashed' : ''}`}
                      style={{ backgroundColor: t.color, borderColor: t.color }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 flex items-center gap-2">
                        {t.name}
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-700">
                          {CATEGORY_LABELS[t.category]}
                        </span>
                        {!t.is_active && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-700">
                            nieaktywny
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500">
                        {formatTimeShort(t.start_time)} – {formatTimeShort(t.end_time)}
                        {t.crosses_midnight && ' (przez północ)'}
                        {t.description ? ` · ${t.description}` : ''}
                      </div>
                    </div>
                    <button
                      onClick={() => startEdit(t)}
                      className="text-sm text-blue-600 hover:underline"
                    >
                      Edytuj
                    </button>
                    <button
                      onClick={() => handleDelete(t, false)}
                      className="text-sm text-gray-500 hover:underline"
                      title="Dezaktywuj"
                    >
                      Dezaktywuj
                    </button>
                    <button
                      onClick={() => handleDelete(t, true)}
                      className="text-sm text-red-600 hover:underline"
                      title="Usuń trwale wraz z przypisaniami"
                    >
                      Usuń
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {editing && (
            <div className="p-4 space-y-3">
              <h3 className="font-semibold">{editing.id ? 'Edycja szablonu' : 'Nowy szablon'}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Nazwa">
                  <input
                    type="text"
                    className="w-full border-gray-300 rounded-md shadow-sm"
                    value={editing.name || ''}
                    onChange={e => setEditing({ ...editing, name: e.target.value })}
                  />
                </Field>
                <Field label="Kategoria">
                  <select
                    className="w-full border-gray-300 rounded-md shadow-sm"
                    value={editing.category || 'shift'}
                    onChange={e => setEditing({ ...editing, category: e.target.value as ShiftCategory })}
                  >
                    {(Object.entries(CATEGORY_LABELS) as [ShiftCategory, string][]).map(([k, label]) => (
                      <option key={k} value={k}>{label}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Start (HH:MM)">
                  <input
                    type="time"
                    className="w-full border-gray-300 rounded-md shadow-sm"
                    value={(editing.start_time || '').slice(0, 5)}
                    onChange={e => setEditing({ ...editing, start_time: e.target.value })}
                  />
                </Field>
                <Field label="Koniec (HH:MM)">
                  <input
                    type="time"
                    className="w-full border-gray-300 rounded-md shadow-sm"
                    value={(editing.end_time || '').slice(0, 5)}
                    onChange={e => setEditing({ ...editing, end_time: e.target.value })}
                  />
                </Field>
                <Field label="Przez północ?">
                  <label className="inline-flex items-center text-sm">
                    <input
                      type="checkbox"
                      className="mr-2"
                      checked={!!editing.crosses_midnight}
                      onChange={e => setEditing({ ...editing, crosses_midnight: e.target.checked })}
                    />
                    Tak, koniec następnego dnia
                  </label>
                </Field>
                <Field label="Aktywny?">
                  <label className="inline-flex items-center text-sm">
                    <input
                      type="checkbox"
                      className="mr-2"
                      checked={editing.is_active !== false}
                      onChange={e => setEditing({ ...editing, is_active: e.target.checked })}
                    />
                    Widoczny i używany w grafiku
                  </label>
                </Field>
                <Field label="Kolor" className="sm:col-span-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      className="h-9 w-12 border border-gray-300 rounded"
                      value={editing.color || '#3b82f6'}
                      onChange={e => setEditing({ ...editing, color: e.target.value })}
                    />
                    <div className="flex gap-1">
                      {DEFAULT_COLORS.map(c => (
                        <button
                          key={c}
                          type="button"
                          className="w-6 h-6 rounded border border-gray-300"
                          style={{ backgroundColor: c }}
                          onClick={() => setEditing({ ...editing, color: c })}
                          title={c}
                        />
                      ))}
                    </div>
                  </div>
                </Field>
                <Field label="Opis (opcjonalnie)" className="sm:col-span-2">
                  <textarea
                    className="w-full border-gray-300 rounded-md shadow-sm"
                    rows={2}
                    value={editing.description || ''}
                    onChange={e => setEditing({ ...editing, description: e.target.value })}
                  />
                </Field>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  className="px-3 py-1.5 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50"
                  onClick={() => setEditing(null)}
                  disabled={busy}
                >
                  Anuluj
                </button>
                <button
                  className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                  onClick={handleSave}
                  disabled={busy}
                >
                  {busy ? 'Zapisywanie…' : 'Zapisz'}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="px-4 py-3 border-t border-gray-200 flex justify-end">
          <button onClick={onClose} className="px-3 py-1.5 text-sm bg-gray-700 text-white rounded hover:bg-gray-800">
            Zamknij
          </button>
        </div>
      </div>
    </div>
  )
}

function Field({ label, children, className = '' }: { label: string, children: React.ReactNode, className?: string }) {
  return (
    <label className={`block text-sm ${className}`}>
      <span className="text-gray-700 font-medium">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  )
}
